import { IconButton } from "@chakra-ui/react";
import React, { memo, useCallback } from "react";

import colors from "app/colors";
import { ReplyIcon } from "modules/icons";
import {
  FileFragment,
  MessageFragment,
  MessageThreadFragment,
} from "gql/graphql";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import SyncoTooltip from "../../../_shared/synco-tooltip";
import { isMessageThreadReadOnlyAndIsReader } from "modules/utils/Functions";
import { useAuth } from "modules/providers/auth-provider";
import { usePubSub } from "modules/providers/pub-sub-provider";

interface IProps {
  draftId: string;
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  file: FileFragment;
}

const MessagePopoverReplyFileButton: React.FC<IProps> = ({
  draftId,
  messageThread,
  message,
  file,
}) => {
  const { me } = useAuth();
  const { createDraft, setReplyQuotedMessage } = useMessageThreadDraft({
    draftId,
  });
  const { publish } = usePubSub();

  const onReplyToFileClicked = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (!file.messageThreadId) return;
      await createDraft({
        type: messageThread.type,
        draftType: "reply",
      });
      setReplyQuotedMessage(message, file);
      publish("focus-quill-editor", draftId);
    },
    [
      file,
      createDraft,
      messageThread.type,
      setReplyQuotedMessage,
      message,
      publish,
      draftId,
    ],
  );

  if (!me) return null;

  if (isMessageThreadReadOnlyAndIsReader(messageThread, me)) {
    return null;
  }

  return (
    <SyncoTooltip
      label="Reply with File"
      wrapInBox={false}
      openDelay={1000}
      placement="top-end"
    >
      <IconButton
        variant="link"
        icon={<ReplyIcon boxSize={5} fill={colors.blue} />}
        onClick={onReplyToFileClicked}
        aria-label="reply to file"
        size="xs"
      />
    </SyncoTooltip>
  );
};

export default memo(MessagePopoverReplyFileButton);
